import React, { useRef, useEffect, useState } from "react";
import { Modal, ModalHeader, ModalBody } from "reactstrap";
import SignatureCanvasLib from "react-signature-canvas";
import { useSignatureManager } from "../customHooks/useSignatureManager";
import {
  CanvasContainer,
  PreviewContainer,
  SignatureModalContent,
  UploadFileInput,
  UploadSection,
} from "../invoiceStyles";
import { PrimaryBgButton, PrimaryWhiteBgButton, WhiteButton } from "../../../styledComponents/buttons";
import { CommonText, CommonFlex, CommonImage, CommonGrid, PageTitle } from "../../../styledComponents/common";
import RoundTab from "../../../components/RoundTabButtons/RoundTab";
import UploadNew from "../../../assets/img/icons/upload_26.svg";

// Handle both ESM and CommonJS module formats
const SignatureCanvas = SignatureCanvasLib?.default || SignatureCanvasLib;

const SignatureModal = ({
  isOpen,
  onClose,
  onSave,
  initialSignature,
  isLoading = false,
}) => {
  const {
    canvasRef,
    isCanvasDrawn,
    clearCanvas,
    exportCanvasAsBase64,
    fileToBase64,
    validateFileSize,
  } = useSignatureManager();

  const [mode, setMode] = useState("draw"); // 'draw' or 'upload'
  const [uploadedSignature, setUploadedSignature] = useState(null);
  const [previewMode, setPreviewMode] = useState(initialSignature ? true : false);
  const fileInputRef = useRef(null);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      if (initialSignature) {
        setPreviewMode(true);
        setMode("draw");
      } else {
        setPreviewMode(false);
        setMode("draw");
        clearCanvas();
        setUploadedSignature(null);
      }
    }
  }, [isOpen]);

  const handleFileInput = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file size
    if (!validateFileSize(file)) {
      alert("File size must be less than 2MB");
      return;
    }

    // Validate file type
    if (!file.type.startsWith("image/")) {
      alert("Please upload an image file");
      return;
    }

    try {
      const base64 = await fileToBase64(file);
      setUploadedSignature(base64);
      setPreviewMode(true);
    } catch (error) {
      alert("Error reading file. Please try again.");
    }
  };

  const handleConfirm = async () => {
    let signatureBase64;

    if (previewMode && uploadedSignature) {
      signatureBase64 = uploadedSignature;
    } else if (isCanvasDrawn()) {
      signatureBase64 = await exportCanvasAsBase64();
    } else if (previewMode && initialSignature) {
      signatureBase64 = initialSignature;
    } else {
      alert("Please draw a signature or upload an image");
      return;
    }

    onSave(signatureBase64);
  };

  const handleRedraw = () => {
    clearCanvas();
    setUploadedSignature(null);
    setPreviewMode(false);
    setMode("draw");
  };

  const handleDeleteSignature = () => {
    handleRedraw();
  };

  const handleDrawMode = () => {
    setMode("draw");
    setUploadedSignature(null);
    setPreviewMode(false);
  };

  const handleUploadMode = () => {
    setMode("upload");
    clearCanvas();
  };

  return (
    <Modal isOpen={isOpen} toggle={onClose} size="md" centered>
      <ModalBody>
        <CommonFlex disableResponsive gap="4px">
            <PageTitle fontSize="18px">Capture Signature</PageTitle>
        </CommonFlex>
        <SignatureModalContent>
          {previewMode && (uploadedSignature || initialSignature) ? (
            <div style={{ textAlign: "center" }}>
              <CommonText marginBottom="12px">
                {uploadedSignature ? "Uploaded Signature" : "Current Signature"}
              </CommonText>
              <PreviewContainer>
                <CommonImage
                  src={uploadedSignature || initialSignature}
                  alt="Signature Preview"
                  style={{ maxWidth: "100%", maxHeight: "250px" }}
                />
              </PreviewContainer>
            </div>
          ) : (
            <>
              <CommonFlex gap="5px"  marginBottom="16px">
                <RoundTab
                  text="Draw Signature"
                  selected={mode === "draw"}
                  onClick={handleDrawMode}
                  showIcon={true}
                />
                <RoundTab
                  text="Upload Image"
                  selected={mode === "upload"}
                  onClick={handleUploadMode}
                  showIcon={true}
                />
              </CommonFlex>
              

              {mode === "draw" ? (
                <div>
                  <CommonText marginBottom="16px" fontSize="14px">
                    Draw your signature below
                  </CommonText>
                  <CanvasContainer>
                    <SignatureCanvas
                      ref={canvasRef}
                      canvasProps={{
                        width: 500,
                        height: 200,
                        style: {
                          border: "2px solid #E0E0E0",
                          borderRadius: "8px",
                          cursor: "crosshair",
                          backgroundColor: "#FAFAFA",
                        },
                      }}
                    />
                  </CanvasContainer>
                  <PrimaryWhiteBgButton
                    onClick={handleRedraw}
                    style={{ marginTop: "16px", width: "100%" }}
                  >
                    Clear Canvas
                  </PrimaryWhiteBgButton>
                </div>
              ) : (
                <UploadSection>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileInput}
                    style={{ display: "none" }}
                  />
                  <UploadFileInput
                    onClick={() => fileInputRef.current?.click()}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        fileInputRef.current?.click();
                      }
                    }}
                  >
                    <CommonImage src={UploadNew} alt="upload" width="24px" />
                    <CommonText fontSize="12px" color="#6776cb" margin="8px 0 0 0">
                      Click to upload signature image
                    </CommonText>
                  </UploadFileInput>
                </UploadSection>
              )}
            </>
          )}
        </SignatureModalContent>
        <CommonFlex justifyContent="flex-end" gap="12px" marginTop="20px">
          {previewMode && (
            <PrimaryWhiteBgButton marginTop='0px' onClick={handleDeleteSignature}>
              Redraw
            </PrimaryWhiteBgButton>
          )}
          <WhiteButton onClick={onClose}>Cancel</WhiteButton>
          <PrimaryBgButton marginTop='0px' onClick={handleConfirm} disabled={isLoading}>
            {isLoading ? "Saving..." : "Confirm Signature"}
          </PrimaryBgButton>
        </CommonFlex>
      </ModalBody>
    </Modal>
  );
};

export default SignatureModal;
